import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';

import { ChatFireService } from '../../../../gen2ai/chatfire.service';
import { AuthenticationService } from '../../../../clari-auth/authentication.service';
import { UserProfile } from '../../../../interfaces-types/user.interface';
import { Conversation } from '../../../../models/chat.model';

type HistoryFabCorner = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

type ConversationGroup = {
  key: string;
  label: string;
  items: Conversation[];
};

@Component({
  selector: 'app-clarity-history',
  templateUrl: './clarity-history.component.html',
  styleUrls: ['./clarity-history.component.scss']
})
export class ClarityHistoryComponent implements OnInit, OnDestroy {
  @Input() fabCorner: HistoryFabCorner = 'bottom-left';
  @Output() openChange = new EventEmitter<boolean>();

  isOpen = false;

  currentUser: UserProfile | null = null;
  isLoggedIn = false;

  loading = false;
  conversations: Conversation[] = [];

  groups: ConversationGroup[] = [];
  activeGroupKey: string | null = null;

  query = '';

  private authSub?: Subscription;
  private loginSub?: Subscription;

  private prevBodyOverflow: string | null = null;

  constructor(
    private router: Router,
    private chatService: ChatFireService,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.authSub = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) this.refresh();
      else this.resetData();
    });

    this.loginSub = this.authService.isLoggedIn$.subscribe(isLoggedIn => {
      this.isLoggedIn = isLoggedIn;
      if (isLoggedIn) this.refresh();
      else this.resetData();
    });

  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.loginSub?.unsubscribe();
    this.unlockBodyScroll();
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    this.openChange.emit(true);
    this.lockBodyScroll();
    if (this.isLoggedIn && this.conversations.length === 0) this.refresh();
  }

  close(): void {
    this.isOpen = false;
    this.openChange.emit(false);
    this.unlockBodyScroll();
  }

  onBackdropClick(): void {
    this.close();
  }

  stopPropagation(evt: MouseEvent): void {
    evt.stopPropagation();
  }

  private lockBodyScroll(): void {
    if (typeof document === 'undefined') return;
    this.prevBodyOverflow = document.body.style.overflow ?? '';
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = this.prevBodyOverflow ?? '';
    this.prevBodyOverflow = null;
  }

  async refresh(): Promise<void> {
    if (!this.isLoggedIn) return;

    try {
      this.loading = true;
      this.conversations = await firstValueFrom(this.chatService.getRecentConversations(40));
      this.buildGroups();
    } catch (err) {
      console.error('[ClarityHistory] Error loading conversations', err);
      this.resetData();
    } finally {
      this.loading = false;
    }
  }

  private resetData(): void {
    this.conversations = [];
    this.groups = [];
    this.activeGroupKey = null;
    this.query = '';
  }

  private buildGroups(): void {
    const map = new Map<string, Conversation[]>();

    for (const c of this.conversations) {
      const d = this.safeDate(c.updatedAt);
      const key = this.dateKey(d);
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }

    const sortedKeys = Array.from(map.keys()).sort((a, b) => (a < b ? 1 : -1));

    this.groups = sortedKeys.map(key => {
      const items = (map.get(key) ?? []).slice().sort((a, b) => {
        const da = this.safeDate(a.updatedAt).getTime();
        const db = this.safeDate(b.updatedAt).getTime();
        return db - da;
      });

      return {
        key,
        label: this.labelForKey(key),
        items
      };
    });

    this.activeGroupKey = this.groups[0]?.key ?? null;
  }

  setActiveGroup(key: string): void {
    this.activeGroupKey = key;
  }

  get activeItems(): Conversation[] {
    const group = this.groups.find(g => g.key === this.activeGroupKey);
    const items = group?.items ?? [];
    const q = (this.query ?? '').trim().toLowerCase();
    if (!q) return items;

    return items.filter(c => {
      const topic = (c.topic ?? '').toLowerCase();
      return topic.includes(q);
    });
  }

  async openConversation(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        await this.router.navigate(['/clarichat'], { queryParams: { conversationId } });
      }
    } catch (err) {
      console.error('[ClarityHistory] Error opening conversation', err);
    }
  }

  truncate(topic: string | undefined | null): string {
    const t = (topic ?? '').trim();
    if (!t) return 'Conversation';
    return t.length <= 34 ? t : t.substring(0, 34) + '...';
  }

  formatTime(value: any): string {
    const d = this.safeDate(value);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  private safeDate(value: any): Date {
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? new Date() : d;
  }

  private dateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private labelForKey(key: string): string {
    const today = this.dateKey(new Date());
    const yd = new Date();
    yd.setDate(yd.getDate() - 1);
    const yesterday = this.dateKey(yd);

    if (key === today) return 'Today';
    if (key === yesterday) return 'Yesterday';

    const [y, m, d] = key.split('-').map(Number);
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
    return dt.toLocaleDateString([], { month: 'long', day: 'numeric' });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }
}
